import type { InternalNetworkCredentialDto } from '@terra/shared-contracts-internal-network';
import { parsePastedAdCredentialIdentity } from '@terra/shared-utils';

export const INTERNAL_NETWORK_CREDENTIAL_TYPES = [
  'DOMAIN_USER',
  'SERVICE_ACCOUNT',
  'NTLM_HASH',
  'AZURE_REFRESH_TOKEN',
] as const satisfies readonly InternalNetworkCredentialDto['credentialType'][];

export const INTERNAL_NETWORK_CREDENTIAL_AUTH_METHODS = [
  'NTLM',
  'KERBEROS',
  'AUTO',
] as const satisfies readonly InternalNetworkCredentialDto['authMethod'][];

export type InternalNetworkCredentialType =
  (typeof INTERNAL_NETWORK_CREDENTIAL_TYPES)[number];

export type InternalNetworkCredentialAuthMethod =
  (typeof INTERNAL_NETWORK_CREDENTIAL_AUTH_METHODS)[number];

export type InternalNetworkCredentialVerificationStatus =
  | 'NOT_VERIFIED'
  | 'VERIFYING'
  | 'VERIFIED'
  | 'FAILED';

export type InternalNetworkCredentialVerificationState = {
  status: InternalNetworkCredentialVerificationStatus;
  lastError?: string;
};

export type InternalNetworkCredentialVerificationResult = {
  success: boolean;
  message: string;
  error?: string;
};

export type InternalNetworkCredentialFormState = {
  name: string;
  username: string;
  password: string;
  ntlmHash: string;
  domain: string;
  credentialType: InternalNetworkCredentialType;
  authMethod: InternalNetworkCredentialAuthMethod;
};

export type InternalNetworkCredentialSaveBody = {
  name: string;
  username: string;
  password?: string;
  ntlmHash?: string;
  domain?: string;
  credentialType: InternalNetworkCredentialType;
  authMethod: InternalNetworkCredentialAuthMethod;
};

export type InternalNetworkCredentialOwnedUpdateBody = {
  name: string;
  username: string;
  domain?: string;
  credentialType: InternalNetworkCredentialType;
  authMethod: InternalNetworkCredentialAuthMethod;
  isOwned: boolean;
};

export const isInternalNetworkCredentialType = (
  value: string,
): value is InternalNetworkCredentialType =>
  (INTERNAL_NETWORK_CREDENTIAL_TYPES as readonly string[]).includes(value);

export const isInternalNetworkCredentialAuthMethod = (
  value: string,
): value is InternalNetworkCredentialAuthMethod =>
  (INTERNAL_NETWORK_CREDENTIAL_AUTH_METHODS as readonly string[]).includes(
    value,
  );

const EMPTY_CREDENTIAL_FORM: InternalNetworkCredentialFormState = {
  name: '',
  username: '',
  password: '',
  ntlmHash: '',
  domain: '',
  credentialType: 'DOMAIN_USER',
  authMethod: 'NTLM',
};

export const createInternalNetworkCredentialForm = ({
  initialCredential,
  defaultDomain,
  defaultCredentialType = 'DOMAIN_USER',
}: {
  initialCredential?: InternalNetworkCredentialDto | null;
  defaultDomain?: string;
  defaultCredentialType?: InternalNetworkCredentialType;
}): InternalNetworkCredentialFormState => {
  if (initialCredential) {
    return {
      name: initialCredential.name,
      username: initialCredential.username,
      password: initialCredential.password ?? '',
      ntlmHash: initialCredential.ntlmHash ?? '',
      domain: initialCredential.domain ?? '',
      credentialType: initialCredential.credentialType,
      authMethod: initialCredential.authMethod,
    };
  }

  return {
    ...EMPTY_CREDENTIAL_FORM,
    domain: defaultDomain ?? '',
    credentialType: defaultCredentialType,
  };
};

export const usesNtlmHashCredential = (
  credentialType: InternalNetworkCredentialType,
): boolean => credentialType === 'NTLM_HASH';

export const usesAzureCredential = (
  credentialType: InternalNetworkCredentialType,
): boolean => credentialType === 'AZURE_REFRESH_TOKEN';

export const isInternalNetworkCredentialFormSubmitDisabled = ({
  form,
  isEditing,
}: {
  form: InternalNetworkCredentialFormState;
  isEditing: boolean;
}): boolean => {
  const usesHash = usesNtlmHashCredential(form.credentialType);
  const usesAzure = usesAzureCredential(form.credentialType);

  return (
    !form.name.trim() ||
    !form.username.trim() ||
    (usesAzure && !form.domain.trim()) ||
    (!isEditing && usesHash && !form.ntlmHash.trim()) ||
    (!isEditing && !usesHash && !form.password.trim())
  );
};

export const toInternalNetworkCredentialSaveBody = (
  form: InternalNetworkCredentialFormState,
): InternalNetworkCredentialSaveBody => {
  const usesHash = usesNtlmHashCredential(form.credentialType);

  return {
    name: form.name.trim(),
    username: form.username.trim(),
    password: usesHash ? undefined : form.password.trim() || undefined,
    ntlmHash: usesHash ? form.ntlmHash.trim() || undefined : undefined,
    domain: form.domain.trim() || undefined,
    credentialType: form.credentialType,
    authMethod: form.authMethod,
  };
};

export const applyInternalNetworkCredentialUsername = ({
  form,
  value,
}: {
  form: InternalNetworkCredentialFormState;
  value: string;
}): InternalNetworkCredentialFormState => {
  if (usesAzureCredential(form.credentialType)) {
    return { ...form, username: value };
  }

  const parsed = parsePastedAdCredentialIdentity(value);

  return {
    ...form,
    username: parsed.username,
    domain: parsed.domain ?? form.domain,
  };
};

export const resolveInternalNetworkCredentialVerificationState = ({
  credential,
  isTesting,
  localState,
}: {
  credential: Pick<InternalNetworkCredentialDto, 'lastVerifiedAt'>;
  isTesting: boolean;
  localState?: InternalNetworkCredentialVerificationState;
}): InternalNetworkCredentialVerificationState => {
  if (isTesting) {
    return { status: 'VERIFYING' };
  }

  if (localState) {
    return localState;
  }

  if (credential.lastVerifiedAt) {
    return { status: 'VERIFIED' };
  }

  return { status: 'NOT_VERIFIED' };
};

export const toInternalNetworkCredentialVerificationState = (
  result: InternalNetworkCredentialVerificationResult,
): InternalNetworkCredentialVerificationState =>
  result.success
    ? { status: 'VERIFIED' }
    : { status: 'FAILED', lastError: result.error ?? result.message };

export const canVerifyInternalNetworkCredential = ({
  canVerify,
  credentialType,
}: {
  canVerify: boolean;
  credentialType?: InternalNetworkCredentialType;
}): boolean => canVerify || credentialType === 'AZURE_REFRESH_TOKEN';

export const toInternalNetworkCredentialOwnedUpdateBody = ({
  credential,
  isOwned,
}: {
  credential: InternalNetworkCredentialDto;
  isOwned: boolean;
}): InternalNetworkCredentialOwnedUpdateBody => ({
  name: credential.name,
  username: credential.username,
  domain: credential.domain ?? undefined,
  credentialType: credential.credentialType,
  authMethod: credential.authMethod,
  isOwned,
});

export const resolveInternalNetworkCredentialAuthMethodLabelKey = (
  credential: Pick<
    InternalNetworkCredentialDto,
    'credentialType' | 'authMethod'
  >,
): `authMethods.${InternalNetworkCredentialAuthMethod}` | null =>
  usesNtlmHashCredential(credential.credentialType) ||
  usesAzureCredential(credential.credentialType)
    ? null
    : `authMethods.${credential.authMethod}`;
